import type { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { goldGlow } from '../../../../shared/glowStyles';
import { Pressable } from '../../common/Touchable';
import { inventoryColors } from '../../Inventory/inventoryTheme';
import {
	FRIENDS_FONT,
	friendsColors,
	hairline,
	inset,
	RADIUS_LG,
	RADIUS_MD,
} from '../friendsTheme';
import { IconCopy, IconShare } from './inviteFriendsIcons';
import { openShareTarget, ShareTarget, ShareTargetKey, shareTargets } from './shareTargets';

/**
 * The web share picker: on web the player chooses the network themselves (the browser's own
 * share sheet only offers whatever the OS happens to have). Sits inside the Invite Friends panel
 * like "How it works?". Native keeps the system share sheet.
 *
 * The brand glyphs are simplified marks drawn here - the Figma file has no share icons yet, so
 * these are placeholders for the designer's set.
 */
const GLYPHS: Record<ShareTargetKey, string> = {
	facebook:
		'M13.4 20v-7.1h2.4l.4-2.8h-2.8V8.3c0-.8.2-1.4 1.4-1.4h1.5V4.4c-.3 0-1.1-.1-2.1-.1-2.1 0-3.6 1.3-3.6 3.7v2.1H8.2v2.8h2.4V20h2.8z',
	messenger:
		'M12 4C7.6 4 4 7.3 4 11.4c0 2.3 1.1 4.4 2.9 5.7V20l2.7-1.5c.8.2 1.6.3 2.4.3 4.4 0 8-3.3 8-7.4S16.4 4 12 4zm.8 9.9l-2-2.2-4 2.2 4.4-4.7 2.1 2.2 3.9-2.2-4.4 4.7z',
	whatsapp:
		'M12 4a8 8 0 0 0-6.9 12l-1.1 4 4.1-1.1A8 8 0 1 0 12 4zm4.3 11.2c-.2.5-1 1-1.5 1-.4.1-.9.1-1.5-.1-.3-.1-.8-.3-1.4-.5-2.4-1-4-3.5-4.1-3.6-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.3.5-.3.6-.3h.5c.1 0 .4 0 .6.4l.8 1.9c.1.1.1.3 0 .5l-.3.4-.4.4c-.1.1-.3.3-.1.5.1.3.6 1 1.3 1.6.9.8 1.6 1 1.9 1.1.2.1.4.1.5-.1l.7-.9c.2-.2.3-.2.5-.1l1.8.9c.3.1.4.2.5.3 0 .1 0 .6-.2 1.1z',
	telegram:
		'M5.4 11.6l11.7-4.5c.5-.2 1 .1.8.9l-2 9.4c-.2.7-.6.8-1.1.5l-3-2.2-1.5 1.4c-.2.2-.3.3-.6.3l.2-3.1 5.6-5c.2-.2-.1-.3-.4-.2l-6.9 4.4-3-.9c-.6-.2-.7-.7.2-1z',
	viber:
		'M12 4c-3.9 0-7 2.6-7 6.6 0 2.2.9 3.7 2.2 4.6V19l2.6-1.8c.7.2 1.4.2 2.2.2 3.9 0 7-2.6 7-6.8S15.9 4 12 4zm3.3 9.6c-.3.6-.9 1.1-1.5 1-2.9-.6-5.2-2.9-5.8-5.8-.1-.6.4-1.2 1-1.5.3-.1.6 0 .8.2l.9 1.3c.2.3.1.6-.1.8l-.4.4c.4 1.1 1.2 1.9 2.3 2.3l.4-.4c.2-.2.5-.3.8-.1l1.3.9c.3.2.4.5.3.9z',
	x: 'M16.9 5h2.4l-5.2 6 6.2 8h-4.8l-3.7-4.9L7.5 19H5.1l5.6-6.4L4.8 5h4.9l3.4 4.5L16.9 5zm-.8 12.6h1.3L9 6.3H7.6l8.5 11.3z',
	email:
		'M5 7h14c.6 0 1 .4 1 1v8c0 .6-.4 1-1 1H5c-.6 0-1-.4-1-1V8c0-.6.4-1 1-1zm.8 1.6v.3l6.2 4 6.2-4v-.3H5.8z',
};

const getCopy = (t: TFunction) => ({
	title: t('T_SITE_INVITE_SHARE_TITLE', { defaultValue: 'Share your invite link' }),
	copy: t('T_SITE_A11Y_COPY_LINK', { defaultValue: 'Copy link' }),
	more: t('T_SITE_INVITE_SHARE_MORE', { defaultValue: 'More' }),
	close: t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' }),
});

type Props = {
	scale: number;
	link: string;
	onCopy: () => void;
	onClose: () => void;
};

export const ShareSheetOverlay = ({ scale, link, onCopy, onClose }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const copy = useMemo(() => getCopy(t), [t]);
	const canUseSystemShare =
		typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function';

	const choose = (target: ShareTarget) => {
		openShareTarget(target, link);
		onClose();
	};

	const systemShare = async () => {
		try {
			await (navigator as any).share({ title: 'Svara', url: link });
		} catch {
			// Dismissed - nothing to do.
		}
		onClose();
	};

	return (
		<View style={styles.root}>
			<Pressable accessibilityRole='button' style={StyleSheet.absoluteFill} onPress={onClose} />

			<View style={styles.card}>
				<Text style={styles.title}>{copy.title}</Text>

				<View style={styles.grid}>
					{shareTargets().map(target => {
						// Brand names stay as they are; only generic labels (Email) carry a key.
						const label = target.labelKey
							? t(target.labelKey, { defaultValue: target.label })
							: target.label;

						return (
							<Pressable
								key={target.key}
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_SHARE_ON', {
									defaultValue: 'Share on {{target}}',
									target: label,
								})}
								onPress={() => choose(target)}
								style={styles.item}
							>
								<View style={[styles.circle, { backgroundColor: target.color }]}>
									<Svg width={26 * scale} height={26 * scale} viewBox='0 0 24 24'>
										<Path d={GLYPHS[target.key]} fill='#FFFFFF' />
									</Svg>
								</View>
								<Text numberOfLines={1} style={styles.label}>
									{label}
								</Text>
							</Pressable>
						);
					})}

					<Pressable
						accessibilityRole='button'
						accessibilityLabel={copy.copy}
						onPress={() => {
							onCopy();
							onClose();
						}}
						style={styles.item}
					>
						<View style={[styles.circle, styles.circleGold]}>
							<IconCopy size={24 * scale} />
						</View>
						<Text numberOfLines={1} style={styles.label}>
							{copy.copy}
						</Text>
					</Pressable>

					{canUseSystemShare ? (
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={copy.more}
							onPress={systemShare}
							style={styles.item}
						>
							<View style={[styles.circle, styles.circleGold]}>
								<IconShare size={22 * scale} />
							</View>
							<Text numberOfLines={1} style={styles.label}>
								{copy.more}
							</Text>
						</Pressable>
					) : null}
				</View>

				<Pressable accessibilityRole='button' onPress={onClose} style={styles.button}>
					<Text style={styles.buttonLabel}>{copy.close}</Text>
				</Pressable>
			</View>
		</View>
	);
};

const ITEM_WIDTH = 66;
const ITEM_GAP = 6;
const COLUMNS = 4;
const CIRCLE = 48;

const createStyles = (scale: number) =>
	StyleSheet.create({
		root: {
			...StyleSheet.absoluteFillObject,
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: inventoryColors.scrim,
			zIndex: 4,
		},
		card: {
			width: 318 * scale,
			gap: 14 * scale,
			padding: inset(16, scale),
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surface,
		},
		title: {
			textAlign: 'center',
			color: friendsColors.accent,
			fontFamily: FRIENDS_FONT,
			fontSize: 20 * scale,
			fontWeight: '900',
			lineHeight: 26 * scale,
		},
		// Exactly four columns wide and centred in the card, so a short last row starts at the left.
		grid: {
			alignSelf: 'center',
			width: (ITEM_WIDTH * COLUMNS + ITEM_GAP * (COLUMNS - 1)) * scale,
			flexDirection: 'row',
			flexWrap: 'wrap',
			rowGap: 12 * scale,
			columnGap: ITEM_GAP * scale,
		},
		item: {
			width: ITEM_WIDTH * scale,
			alignItems: 'center',
			gap: 6 * scale,
		},
		circle: {
			width: CIRCLE * scale,
			height: CIRCLE * scale,
			borderRadius: (CIRCLE / 2) * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
		circleGold: {
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
		},
		label: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: 11 * scale,
			fontWeight: '700',
		},
		button: {
			height: 40 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			...goldGlow,
		},
		buttonLabel: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: 15 * scale,
			fontWeight: '900',
		},
	});

export default ShareSheetOverlay;
